// F-088 — Communications collectives d'immeuble et incidents des parties communes.
import { useState } from "react";
import { del, post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, today } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Empty, ErrorBox, Fil, Input, KeyValues, Loading, Modal, Notice, PageHeader, Select, SimBadge, StatusChip, Table, Tabs, Textarea, fieldError } from "@core/ui";
import { BuildingSelect, PropertySelect, required, useAction, useRefs } from "./shared";

type Announcement = { id: string; building_id: string; building_label: string; incident_id: string | null; incident_title: string | null; subject: string; body: string; status: "brouillon" | "envoyee"; recipients_count: number; sent_at: string | null; created_at: string };
type Audience = { leases: number; recipients: number; without_email: number; lots: { lease_id: string; property_label: string; tenants: number; without_email: number }[] };
type Incident = { id: string; building_id: string; building_label: string; title: string; description: string; status: "signale" | "en_cours" | "resolu"; reported_on: string; resolved_on: string | null; reports_count: number; lots_count: number };

const INCIDENT_STATUS: Record<string, string> = { signale: "Signalé", en_cours: "Intervention en cours", resolu: "Résolu" };
const RECEIPT: Record<string, [string, "neutre" | "encre" | "succes" | "attention" | "erreur" | "simulateur"]> = {
  simule: ["Envoi simulé", "simulateur"],
  envoye: ["Envoyé", "succes"],
  echec: ["Échec d’envoi", "erreur"],
  sans_adresse: ["Pas d’adresse e-mail", "attention"],
  doublon: ["Adresse déjà servie", "neutre"],
};
const CHANNELS = [
  { value: "telephone", label: "Téléphone" },
  { value: "courriel", label: "Courriel" },
  { value: "message", label: "Message" },
  { value: "visite", label: "Visite" },
  { value: "autre", label: "Autre" },
];

export function CommunicationsPage() {
  const [tab, setTab] = useState<"annonces" | "incidents">("annonces");
  const [prefill, setPrefill] = useState<Incident | null>(null);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Immeubles"
        title="Communications et incidents communs"
        intro="Informez les occupants d’un immeuble par des messages individuels, sans jamais révéler les coordonnées des uns aux autres, et suivez une même panne signalée par plusieurs lots."
      />
      <Tabs value={tab} onChange={setTab} items={[{ value: "annonces", label: "Annonces" }, { value: "incidents", label: "Incidents des parties communes" }]} />
      {tab === "annonces" ? (
        <Announcements prefill={prefill} onPrefillUsed={() => setPrefill(null)} />
      ) : (
        <Incidents
          onInform={(i) => {
            setPrefill(i);
            setTab("annonces");
          }}
        />
      )}
    </div>
  );
}

function Announcements({ prefill, onPrefillUsed }: { prefill: Incident | null; onPrefillUsed: () => void }) {
  const org = useOrg();
  const q = useGet<{ items: Announcement[] }>(org.api("/building-announcements"));
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canEdit = org.can("operate");
  const showForm = creating || !!prefill;
  return (
    <div className="stack">
      <Notice tone="simulateur" title={<>Envois <SimBadge mode="simulateur" /></>}>
        Aucun prestataire d’envoi n’est branché : les messages sont visibles dans « Envois » et ne quittent pas Bailivo.
      </Notice>
      {canEdit ? (
        <div>
          <Button onClick={() => setCreating(true)}>Nouvelle annonce</Button>
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucune annonce." /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Annonces">
          <thead>
            <tr>
              <th>Objet</th>
              <th>Immeuble</th>
              <th>État</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.subject}
                  {a.incident_title ? <span className="finances-sous">Incident : {a.incident_title}</span> : null}
                </td>
                <td>{a.building_label}</td>
                <td>{a.status === "envoyee" ? <StatusChip status="conforme" label={`Envoyée le ${dateTime(a.sent_at)} (${a.recipients_count})`} /> : <Badge tone="encre">Brouillon</Badge>}</td>
                <td>
                  <Button small variant="discret" onClick={() => setOpen(a.id)}>Ouvrir</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {showForm ? (
        <AnnouncementModal
          announcement={null}
          incident={prefill}
          onClose={() => {
            setCreating(false);
            onPrefillUsed();
          }}
          onCreated={(id) => setOpen(id)}
        />
      ) : null}
      {open ? <AnnouncementDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function AnnouncementModal({ announcement, incident, onClose, onCreated }: { announcement: Announcement | null; incident: Incident | null; onClose: () => void; onCreated?: (id: string) => void }) {
  const org = useOrg();
  const refs = useRefs();
  const an = announcement;
  const [f, setF] = useState({
    building_id: an?.building_id ?? incident?.building_id ?? "",
    incident_id: an?.incident_id ?? incident?.id ?? "",
    subject: an?.subject ?? (incident ? `Parties communes : ${incident.title}` : ""),
    body: an?.body ?? (incident ? `Bonjour,\n\nNous avons bien noté l’incident « ${incident.title} ». ` : "Bonjour,\n\n"),
  });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title={an ? "Modifier l’annonce" : "Nouvelle annonce d’immeuble"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const missing = required([[!!f.building_id, "building_id", "Choisissez l’immeuble."]]);
              if (missing) return a.setError(missing);
              const body = { ...f, incident_id: f.incident_id || null };
              const r = await a.run<{ announcement: Announcement }>(() => (an ? put(org.api(`/building-announcements/${an.id}`), body) : post(org.api("/building-announcements"), body)), { success: "Brouillon enregistré.", refresh: [org.api("/building-announcements")] });
              if (r) {
                onClose();
                if (!an) onCreated?.(r.announcement.id);
              }
            }}
          >
            Enregistrer le brouillon
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <BuildingSelect refs={refs.data} value={f.building_id} error={fieldError(a.error, "building_id")} onChange={(v) => setF({ ...f, building_id: v })} empty="Choisir…" />
        <Input label="Objet" required value={f.subject} error={fieldError(a.error, "subject")} onChange={(e) => setF({ ...f, subject: e.target.value })} />
        <Textarea label="Message" required rows={8} value={f.body} error={fieldError(a.error, "body")} onChange={(e) => setF({ ...f, body: e.target.value })} />
        <p className="small muted">Ne mentionnez ni la situation personnelle ni les coordonnées d’un occupant : le même message part à chacun.</p>
      </div>
    </Modal>
  );
}

function AnnouncementDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<{ announcement: Announcement; receipts: { lease_id: string; property_label: string; status: string; created_at: string }[]; audience: Audience | null }>(org.api(`/building-announcements/${id}`));
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const a = useAction();
  const d = q.data;
  const canEdit = org.can("operate");
  const refresh = [org.api("/building-announcements"), org.api("/common-incidents")];
  return (
    <Modal open onClose={onClose} title={d?.announcement.subject ?? "Annonce"}>
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error ?? a.error} />
        {d ? (
          <>
            <KeyValues items={[["Immeuble", d.announcement.building_label], ["État", d.announcement.status === "envoyee" ? `Envoyée le ${dateTime(d.announcement.sent_at)}` : "Brouillon"]]} />
            <div className="finances-element">
              <p style={{ whiteSpace: "pre-wrap" }}>{d.announcement.body}</p>
            </div>
            {d.audience ? (
              <>
                <h3>Destinataires</h3>
                <p>
                  {d.audience.recipients} personne{d.audience.recipients > 1 ? "s" : ""} dans {d.audience.leases} logement{d.audience.leases > 1 ? "s" : ""} loué{d.audience.leases > 1 ? "s" : ""}. Chacune recevra un message individuel.
                  {d.audience.without_email ? ` ${d.audience.without_email} occupant${d.audience.without_email > 1 ? "s n’ont" : " n’a"} pas d’adresse e-mail : informez-le${d.audience.without_email > 1 ? "s" : ""} autrement.` : ""}
                </p>
                <ul className="list-plain">
                  {d.audience.lots.map((l) => (
                    <li key={l.lease_id} className="small">
                      {l.property_label} — {l.tenants} locataire{l.tenants > 1 ? "s" : ""}
                      {l.without_email ? `, dont ${l.without_email} sans adresse` : ""}
                    </li>
                  ))}
                </ul>
                {canEdit ? (
                  <div className="finances-actions">
                    <Button variant="discret" onClick={() => a.run(() => del(org.api(`/building-announcements/${id}`)), { success: "Brouillon supprimé.", refresh }).then((r) => r && onClose())}>Supprimer</Button>
                    <Button variant="secondaire" onClick={() => setEditing(true)}>Modifier</Button>
                    <Button disabled={d.audience.recipients === 0} onClick={() => setConfirming(true)}>Envoyer</Button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <h3>Accusés techniques</h3>
                <Table caption="Accusés d’envoi">
                  <thead>
                    <tr>
                      <th>Logement</th>
                      <th>Envoi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.receipts.map((r, i) => {
                      const [label, tone] = RECEIPT[r.status] ?? [r.status, "neutre"];
                      return (
                        <tr key={i}>
                          <td>{r.property_label}</td>
                          <td><Badge tone={tone}>{label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <p className="small muted">Une annonce envoyée ne peut pas être renvoyée : rédigez-en une nouvelle si nécessaire.</p>
              </>
            )}
          </>
        ) : null}
        {confirming && d?.audience ? (
          <Modal
            open
            onClose={() => setConfirming(false)}
            title="Confirmer l’envoi"
            footer={
              <>
                <Button variant="secondaire" onClick={() => setConfirming(false)}>Annuler</Button>
                <Button busy={a.busy} onClick={async () => {
                  const r = await a.run(() => post(org.api(`/building-announcements/${id}/send`)), { success: "Annonce envoyée individuellement.", refresh });
                  if (r) setConfirming(false);
                }}>Envoyer à {d.audience.recipients} personne{d.audience.recipients > 1 ? "s" : ""}</Button>
              </>
            }
          >
            <p>Chaque destinataire reçoit son propre message. L’envoi n’est fait qu’une fois.</p>
          </Modal>
        ) : null}
        {editing && d ? <AnnouncementModal announcement={d.announcement} incident={null} onClose={() => setEditing(false)} /> : null}
      </div>
    </Modal>
  );
}

function Incidents({ onInform }: { onInform: (i: Incident) => void }) {
  const org = useOrg();
  const refs = useRefs();
  const [building, setBuilding] = useState("");
  const q = useGet<{ items: Incident[] }>(org.api(`/common-incidents${building ? `?building_id=${building}` : ""}`));
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canEdit = org.can("operate");
  return (
    <div className="stack">
      <div className="finances-filtres">
        <BuildingSelect refs={refs.data} value={building} onChange={setBuilding} empty="Tous les immeubles" />
      </div>
      {canEdit ? (
        <div>
          <Button onClick={() => setCreating(true)}>Signaler un incident commun</Button>
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun incident des parties communes." /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Incidents communs">
          <thead>
            <tr>
              <th>Incident</th>
              <th>Immeuble</th>
              <th>Signalements</th>
              <th>État</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((i) => (
              <tr key={i.id}>
                <td>
                  {i.title}
                  <span className="finances-sous">depuis le {date(i.reported_on)}</span>
                </td>
                <td>{i.building_label}</td>
                <td>{i.reports_count} ({i.lots_count} lot{i.lots_count > 1 ? "s" : ""})</td>
                <td><Badge tone={i.status === "resolu" ? "succes" : i.status === "en_cours" ? "encre" : "attention"}>{INCIDENT_STATUS[i.status]}</Badge></td>
                <td><Button small variant="discret" onClick={() => setOpen(i.id)}>Suivre</Button></td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {creating ? <IncidentModal onClose={() => setCreating(false)} /> : null}
      {open ? <IncidentDetail id={open} onClose={() => setOpen(null)} onInform={onInform} /> : null}
    </div>
  );
}

function IncidentModal({ onClose }: { onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const [f, setF] = useState({ building_id: "", title: "", description: "", reported_on: today(), property_id: "", channel: "telephone" });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Incident des parties communes"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={async () => {
            const missing = required([[!!f.building_id, "building_id", "Choisissez l’immeuble."]]);
            if (missing) return a.setError(missing);
            const r = await a.run(() => post(org.api("/common-incidents"), { ...f, property_id: f.property_id || null, reported_on: f.reported_on || null }), { success: "Incident enregistré.", refresh: [org.api("/common-incidents")] });
            if (r) onClose();
          }}>Enregistrer</Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <BuildingSelect refs={refs.data} value={f.building_id} error={fieldError(a.error, "building_id")} onChange={(v) => setF({ ...f, building_id: v, property_id: "" })} empty="Choisir…" />
        <Input label="Intitulé" required placeholder="Ex. Porte du hall bloquée" value={f.title} error={fieldError(a.error, "title")} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Textarea label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className="form-row">
          <Input label="Signalé le" type="date" value={f.reported_on} error={fieldError(a.error, "reported_on")} onChange={(e) => setF({ ...f, reported_on: e.target.value })} />
          <PropertySelect refs={refs.data} buildingId={f.building_id || undefined} value={f.property_id} error={fieldError(a.error, "property_id")} onChange={(v) => setF({ ...f, property_id: v })} label="Signalé depuis le lot" empty="Non précisé" />
          <Select label="Canal" value={f.channel} options={CHANNELS} onChange={(e) => setF({ ...f, channel: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

function IncidentDetail({ id, onClose, onInform }: { id: string; onClose: () => void; onInform: (i: Incident) => void }) {
  const org = useOrg();
  const refs = useRefs();
  const q = useGet<{ incident: Incident; reports: { id: string; property_label: string | null; reported_on: string; channel: string; note: string }[]; events: { id: string; status: string; note: string; created_at: string }[]; announcements: { id: string; subject: string; status: string; sent_at: string | null }[]; notice: string }>(org.api(`/common-incidents/${id}`));
  const [report, setReport] = useState({ property_id: "", reported_on: today(), channel: "telephone", note: "" });
  const [status, setStatus] = useState({ status: "en_cours", note: "" });
  const a = useAction();
  const d = q.data;
  const canEdit = org.can("operate");
  const refresh = [org.api("/common-incidents")];
  return (
    <Modal open onClose={onClose} title={d?.incident.title ?? "Incident"}>
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error ?? a.error} />
        {d ? (
          <>
            <Notice>{d.notice}</Notice>
            <KeyValues items={[["Immeuble", d.incident.building_label], ["État", INCIDENT_STATUS[d.incident.status]], ["Description", d.incident.description || "—"]]} />
            <h3>Suivi</h3>
            <Fil steps={d.events.map((e, i) => ({ title: INCIDENT_STATUS[e.status], text: `${dateTime(e.created_at)}${e.note ? ` — ${e.note}` : ""}`, state: i === d.events.length - 1 ? "actuel" : "fait" }))} />
            <h3>Signalements rattachés ({d.reports.length})</h3>
            <ul className="list-plain">
              {d.reports.map((r) => (
                <li key={r.id} className="small">
                  {date(r.reported_on)} — {r.property_label ?? "lot non précisé"} ({CHANNELS.find((c) => c.value === r.channel)?.label ?? r.channel}){r.note ? ` : ${r.note}` : ""}
                </li>
              ))}
            </ul>
            {canEdit && d.incident.status !== "resolu" ? (
              <div className="stack-sm">
                <p className="small"><strong>Même panne signalée par un autre lot</strong></p>
                <div className="form-row">
                  <PropertySelect refs={refs.data} buildingId={d.incident.building_id} value={report.property_id} error={fieldError(a.error, "property_id")} onChange={(v) => setReport({ ...report, property_id: v })} label="Lot" empty="Non précisé" />
                  <Input label="Date" type="date" value={report.reported_on} onChange={(e) => setReport({ ...report, reported_on: e.target.value })} />
                  <Select label="Canal" value={report.channel} options={CHANNELS} onChange={(e) => setReport({ ...report, channel: e.target.value })} />
                </div>
                <Input label="Note" value={report.note} onChange={(e) => setReport({ ...report, note: e.target.value })} />
                <div>
                  <Button small variant="secondaire" busy={a.busy} onClick={() => a.run(() => post(org.api(`/common-incidents/${id}/reports`), { ...report, property_id: report.property_id || null, reported_on: report.reported_on || null }), { success: "Signalement rattaché.", refresh })}>
                    Rattacher le signalement
                  </Button>
                </div>
              </div>
            ) : null}
            {canEdit ? (
              <div className="stack-sm">
                <p className="small"><strong>Mettre à jour l’état</strong></p>
                <div className="form-row">
                  <Select label="Nouvel état" value={status.status} options={Object.entries(INCIDENT_STATUS).map(([value, label]) => ({ value, label }))} onChange={(e) => setStatus({ ...status, status: e.target.value })} />
                  <Input label="Note" value={status.note} onChange={(e) => setStatus({ ...status, note: e.target.value })} />
                </div>
                <div className="finances-actions">
                  <Button variant="secondaire" onClick={() => { onClose(); onInform(d.incident); }}>Informer les occupants</Button>
                  <Button busy={a.busy} onClick={() => a.run(() => post(org.api(`/common-incidents/${id}/status`), status), { success: "État mis à jour.", refresh })}>Enregistrer l’état</Button>
                </div>
              </div>
            ) : null}
            {d.announcements.length ? (
              <p className="small muted">Annonces liées : {d.announcements.map((x) => `${x.subject} (${x.status === "envoyee" ? `envoyée le ${dateTime(x.sent_at)}` : "brouillon"})`).join(", ")}</p>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
